"use client";

import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";

// components
import GenericSelectionModal from "@/components/GenericSelectionModal";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocale } from "next-intl";
import TripDatePicker from "../common/tripDatepicker";
import GenericAutoComplete from "./GenericAutoComplete";

// configs
import {
  busPassengerDisplayFormatter,
  defaultBusPassengerConfig,
} from "@/configs/bus/search";

// icons

// i18n
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

// hooks
import { useBusSearch } from "@/hooks/useBusSearch";
import { useRouter } from "@/i18n/navigation";

// utils
import { useIsMobile } from "@/hooks/useIsMobile";
import { getPassengerTypeBreakdownVisibility } from "@/utils/bus/appConfigUtils";
import { ensureFutureDate } from "@/utils/bus/dateUtils";
import { dateFormats } from "@/utils/dateFormatter";

const Form = ({
  initialDepartureCityCode,
  initialReturnCityCode,
  initialDepartureDate,
  initialPassengers,
  searchButtonTextClassName = "inline md:hidden lg:inline",
  searchButtonTurnaIconClassName = "!inline-block md:!hidden lg:!block",
  searchButtonMagnifierIconClassName = "!hidden md:!inline-block lg:!hidden",
  leftSideContentWrapperClassname = "md:w-3/5",
  rightSideContentWrapperClassname = "md:w-2/5",
  viewportHeightWrapperActive = false,
} = {}) => {
  const showPassengerTypeBreakdown = getPassengerTypeBreakdownVisibility();

  const isMobile = useIsMobile();

  const locale = useLocale();

  const t = useTranslationDetector();
  const tForm = useTranslationDetector();
  const router = useRouter();

  // Bus-specific storage key
  const STORAGE_KEY = "latestBusSearches";

  // Initial data objesini oluştur (flight search ile aynı mantık)
  const initialData = {
    // useTomorrowAsDefault: true, // true ise Yarın seçili gelecek, default false (bugün)
  };
  if (initialDepartureCityCode)
    initialData.initialDepartureCityCode = initialDepartureCityCode;
  if (initialReturnCityCode)
    initialData.initialReturnCityCode = initialReturnCityCode;
  if (initialDepartureDate) initialData.departureDate = initialDepartureDate;
  if (initialPassengers) initialData.passengers = initialPassengers;
  initialData.storageKey = STORAGE_KEY;

  // Use bus search hook
  const {
    fromCity,
    toCity,
    departureDate,
    passengers,
    latestBusSearches,
    popularDestinations,
    ensurePopularLoaded,
    setDepartureDate,
    setPassengersAndCabinClass,
    toggleFromAndTo,
    searchForBusTerminals,
    onSelectFromCity,
    onSelectToCity,
    validateForm,
    generateBusSearchUrl,
    publicHolidays,
    refreshPublicHolidays,
    useTomorrowAsDefault,
  } = useBusSearch(initialData);

  const [validationErrors, setValidationErrors] = useState({});
  const passengerModalRef = useRef(null);
  const dateSelectionRef = useRef(null);

  const passengerConfig = useMemo(() => {
    return defaultBusPassengerConfig;
  }, []);

  const handleFromCitySelect = useCallback(
    (city) => {
      onSelectFromCity(city);
      setValidationErrors((prev) => ({ ...prev, departureCity: null }));
    },
    [onSelectFromCity],
  );

  const clearFromCitySelection = useCallback(() => {
    onSelectFromCity(null);
  }, [onSelectFromCity]);

  const handleToCitySelect = useCallback(
    (city) => {
      onSelectToCity(city);
      setValidationErrors((prev) => ({ ...prev, returnCity: null }));
    },
    [onSelectToCity],
  );

  const clearToCitySelection = useCallback(() => {
    onSelectToCity(null);
  }, [onSelectToCity]);

  const handleDateChange = useCallback(
    (data) => {
      setDepartureDate(data.departureDate);
      setValidationErrors((prev) => ({ ...prev, departureDate: null }));
    },
    [setDepartureDate],
  );

  // Handle search history item selection
  const handleSearchHistorySelect = useCallback(
    (search, onClosePortal) => {
      // Fill all form fields with the selected search data
      if (search.departureCity) {
        onSelectFromCity(search.departureCity);
      }
      if (search.returnCity) {
        onSelectToCity(search.returnCity);
      }
      if (search.departureDateRaw) {
        // Parse the date from localStorage and ensure it's not a past date
        const parsedDate = ensureFutureDate(search.departureDateRaw);
        setDepartureDate(parsedDate);
      }
      // Clear any validation errors
      setValidationErrors({});

      // Mobile portal'ı kapat
      if (onClosePortal) {
        onClosePortal();
      }
    },
    [onSelectFromCity, onSelectToCity, setDepartureDate],
  );

  // Render function for default content (when not searching)
  const renderDefaultContent = useCallback(
    ({ onSelectItem, onClosePortal }) => {
      return (
        <div className="flex flex-col gap-5 text-sm">
          {latestBusSearches && (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("bus.recentSearches")}
              </span>
              <div className="flex w-full flex-col items-start justify-between gap-4">
                {latestBusSearches.map((search, index) => (
                  <button
                    className="flex w-full cursor-pointer items-start justify-between gap-6"
                    onClick={() =>
                      handleSearchHistorySelect(search, onClosePortal)
                    }
                    key={index}
                  >
                    <div className="flex items-center gap-4">
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["clock-rotate-left"]}
                      />
                      <div className="flex flex-col gap-1 text-left">
                        <div className="flex items-center justify-start gap-[6px] font-semibold">
                          <span>
                            {search.departureCity.value.split("-")[0].trim()}
                          </span>
                          {search.isOneWay ? (
                            <FontAwesomeIcon
                              icon={byPrefixAndName.fak["half-right-arrow"]}
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={
                                byPrefixAndName.fak["half-round-trip-arrows"]
                              }
                            />
                          )}

                          <span>
                            {search.returnCity.value.split("-")[0].trim()}
                          </span>
                        </div>
                        <div className="text-foreground-muted">
                          {search.isOneWay
                            ? dateFormats.standard(
                                search.departureDateRaw,
                                locale,
                              )
                            : `${dateFormats.standard(search.departureDateRaw, locale)} - ${dateFormats.standard(search.returnDateRaw, locale)}`}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Popular destinations */}
          {!popularDestinations || popularDestinations.length === 0 ? (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("bus.searchPopularLocations")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {/* Placeholder chips */}
                <span className="bg-background-400 h-8 w-24 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-20 animate-pulse rounded-lg" />
                <span className="bg-background-400 h-8 w-28 animate-pulse rounded-lg" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-start justify-between gap-2">
              <span className="text-center text-gray-500">
                {tForm("bus.searchPopularLocations")}
              </span>
              <div className="flex w-full flex-wrap gap-3">
                {popularDestinations.map((item, index) => (
                  <button
                    key={index}
                    className="border-border cursor-pointer rounded-lg border px-3 py-[8px] text-base"
                    onClick={() => onSelectItem(item.AutoCompleteResult)}
                  >
                    {item.CityName || item.value || item.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
    [latestBusSearches, popularDestinations, tForm, handleSearchHistorySelect],
  );

  // Render function for no results
  const renderNoResults = useCallback(
    ({ isLoading, inputValue, clearInput }) => {
      return (
        <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
          <div className="flex flex-col gap-2">
            <span className="text-center text-lg font-semibold">
              {tForm("bus.noResultsFound")}
            </span>
            <span className="text-center text-sm">
              {tForm("bus.changeSearchCriteria")}
            </span>
          </div>
        </div>
      );
    },
    [tForm],
  );

  // Render function for search results
  const renderSearchResults = useCallback(({ searchResults, onSelectItem }) => {
    return (
      <div className="flex flex-col gap-4 text-sm">
        {searchResults?.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 p-10 text-sm">
            <div className="flex flex-col gap-2">
              <span className="text-center text-lg font-semibold">
                {tForm("bus.noResultsFound")}
              </span>
              <span className="text-center text-sm">
                {tForm("bus.changeSearchCriteria")}
              </span>
            </div>
          </div>
        )}
        {searchResults?.length > 0 &&
          searchResults?.map((searchResult) => (
            <button
              className="flex cursor-pointer items-start justify-between gap-6"
              key={searchResult.id}
              onClick={() => onSelectItem(searchResult)}
            >
              <div className="flex items-center gap-4 text-left">
                <FontAwesomeIcon icon={byPrefixAndName.fas["location-dot"]} />
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center justify-start gap-[6px] font-semibold">
                    {searchResult.value}
                  </div>
                  <div className="text-foreground-muted">
                    {searchResult.label.split("-")[0]}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">{searchResult.code}</div>
            </button>
          ))}
      </div>
    );
  }, []);

  const handlePassengerSelect = useCallback(
    (values) => {
      setPassengersAndCabinClass({ passengers: values, cabinClass: "economy" });
    },
    [setPassengersAndCabinClass],
  );

  const toggleCities = useCallback(() => {
    toggleFromAndTo();
    // Toggle sonrasında validation hatalarını temizle
    // Çünkü artık yeni şehir yerleşimi var ve hata durumu değişmiş olabilir
    setValidationErrors({});
  }, [toggleFromAndTo]);

  const validateAndSearch = useCallback(() => {
    const errors = validateForm();

    if (Object.keys(errors).length === 0) {
      // Validasyon başarılı - hatalar varsa temizle
      setValidationErrors({});

      // Son arama bilgilerini localStorage'a kaydet
      const newSearch = {
        departureCity: fromCity,
        returnCity: toCity,
        isOneWay: true,
        departureDateRaw: departureDate,
        departureDate: departureDate?.toLocaleDateString("tr-TR"),
        returnDate: null,
      };

      // Mevcut arama geçmişini al
      const existingSearches = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "[]",
      );

      // Yeni kaydın "eşitlik" anahtarını oluştur (şehir + tarih)
      const newSearchKey = `${fromCity?.id || fromCity?.code || fromCity?.value}-${
        toCity?.id || toCity?.code || toCity?.value
      }-${
        departureDate ? new Date(departureDate).toISOString().slice(0, 10) : ""
      }`;

      // Aynı kombinasyona sahip olan eski kayıtları çıkar
      const filteredSearches = existingSearches.filter((search) => {
        const existingKey = `${search.departureCity?.id || search.departureCity?.code || search.departureCity?.value}-${
          search.returnCity?.id ||
          search.returnCity?.code ||
          search.returnCity?.value
        }-${
          search.departureDateRaw
            ? new Date(search.departureDateRaw).toISOString().slice(0, 10)
            : ""
        }`;

        return existingKey !== newSearchKey;
      });

      // Yeni aramayı array'in başına ekle (uniq hale gelmiş liste)
      const updatedSearches = [newSearch, ...filteredSearches];

      // localStorage'a kaydet
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSearches));

      // URL oluştur ve yönlendir
      const url = generateBusSearchUrl();
      if (url) {
        //window.location.replace(url); // no ajx ama geri tuşu çalışmıyor bu yontemde
        window.location.href = url;
        //router.push(url);
      }
    } else {
      // Validasyon başarısız - hataları göster
      setValidationErrors(errors);
    }
  }, [
    validateForm,
    fromCity,
    toCity,
    departureDate,
    generateBusSearchUrl,
    router,
  ]);

  ////// Desktop için scroll to form
  const wrapperRef = useRef(null);
  const handleOpenedDropdown = () => {
    if (!isMobile) {
      const elm = wrapperRef?.current;
      if (elm) {
        const inputRect = elm.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = inputRect.top + scrollTop - 200;

        window.scrollTo({
          top: targetPosition,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-4 md:gap-5">
      <div className="shadow-medium flex flex-col gap-0 rounded-xl bg-white md:gap-6 md:rounded-none md:bg-transparent md:shadow-none">
        <div className="flex w-full flex-col md:flex-row md:gap-4">
          <div className="md:shadow-medium flex flex-col items-center rounded-lg bg-white shadow-none md:w-4/4 md:flex-row">
            <div
              className={clsx(
                "relative z-[1] flex w-full flex-col items-end md:static md:flex-row md:items-center",
                leftSideContentWrapperClassname,
              )}
            >
              {/* AutoComplete - Nereden */}
              <div className="w-full md:w-1/2">
                <GenericAutoComplete
                  inputClassName="font-semibold! text-base!"
                  id="bus-from"
                  label={tForm("bus.from")}
                  customInputPlaceholder={tForm(
                    "bus.autoCompleteInputCustomPlaceholder",
                  )}
                  searchFunction={searchForBusTerminals}
                  onSelect={handleFromCitySelect}
                  onClearSelection={clearFromCitySelection}
                  value={fromCity?.value}
                  error={validationErrors.departureCity}
                  errorMessage={tForm(
                    "bus.autoCompleteInputDepartureCityRequiredMessage",
                  )}
                  translationNamespace="bus"
                  renderDefaultContent={renderDefaultContent}
                  onOpen={() => {
                    ensurePopularLoaded();
                    handleOpenedDropdown();
                  }}
                  renderSearchResults={renderSearchResults}
                  placeholderPortalInput={tForm(
                    "bus.autoCompletePortalInputPlaceholder",
                  )}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  //renderNoResults={renderNoResults}
                  debounce={300}
                  minQueryLength={2}
                />
              </div>

              <span className="relative z-[11] h-[1px] w-[1px] overflow-visible md:h-6 md:w-6">
                <button
                  onClick={toggleCities}
                  className="border-border-weaker flex h-11 w-11 -translate-x-[60px] -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border-2 bg-white md:-translate-x-[10px] md:-translate-y-[10px]"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["arrow-up-arrow-down"]}
                    className="md:rotate-90"
                  />
                </button>
              </span>

              {/* AutoComplete - Nereye */}
              <div className="w-full md:w-1/2">
                <GenericAutoComplete
                  inputClassName="font-semibold! text-base!"
                  id="bus-to"
                  label={tForm("bus.to")}
                  customInputPlaceholder={tForm(
                    "bus.autoCompleteInputCustomPlaceholder",
                  )}
                  searchFunction={searchForBusTerminals}
                  onSelect={handleToCitySelect}
                  onClearSelection={clearToCitySelection}
                  value={toCity?.value}
                  error={validationErrors.returnCity}
                  errorMessage={tForm(
                    "bus.autoCompleteInputReturnCityRequiredMessage",
                  )}
                  translationNamespace="bus"
                  renderDefaultContent={renderDefaultContent}
                  onOpen={() => {
                    ensurePopularLoaded();
                    handleOpenedDropdown();
                  }}
                  renderSearchResults={renderSearchResults}
                  placeholderPortalInput={tForm(
                    "bus.autoCompletePortalInputPlaceholder",
                  )}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                  //renderNoResults={renderNoResults}
                  debounce={300}
                  minQueryLength={2}
                />
              </div>
            </div>

            <span className="bg-border mx-1 hidden h-[34px] w-[1px] md:block"></span>

            {/* Date Selection - Oneway */}
            <div
              className={clsx(
                "border-primary-weak flex w-full items-center border-b pr-3 md:border-0 md:pr-[21px]",
                rightSideContentWrapperClassname,
              )}
            >
              <div className="w-full">
                <TripDatePicker
                  inputClassName="font-semibold! text-base!"
                  quickButtonsWrapperClassName="pr-[6px] md:pr-0!"
                  ref={dateSelectionRef}
                  tripType="oneway"
                  translationNamespace="bus"
                  initialDepartureDate={departureDate}
                  onChange={handleDateChange}
                  showQuickButtons={true}
                  publicHolidays={publicHolidays}
                  onDropdownOpen={handleOpenedDropdown}
                  onOpen={refreshPublicHolidays}
                  useTomorrowAsDefault={useTomorrowAsDefault}
                  viewportHeightWrapperActive={viewportHeightWrapperActive}
                />
              </div>
            </div>
          </div>

          {/* Passenger Selection */}
          {showPassengerTypeBreakdown && (
            <div className="w-full md:w-1/4 md:max-w-[120px]">
              <GenericSelectionModal
                buttonWrapperClassName="font-semibold! text-base!"
                ref={passengerModalRef}
                config={passengerConfig}
                label={tForm("bus.passengerTitle")}
                placeholder={tForm("bus.passengerSelect")}
                value={passengers}
                onSelect={handlePassengerSelect}
                displayFormatter={(values) =>
                  busPassengerDisplayFormatter(values, tForm)
                }
                applyButtonText={tForm("bus.passengerApply")}
                onDropdownOpen={handleOpenedDropdown}
                viewportHeightWrapperActive={viewportHeightWrapperActive}
              />
            </div>
          )}

          {/* Search Button */}
          <div className="p-4 md:p-0">
            <button
              type="button"
              onClick={validateAndSearch}
              className="bg-primary hover:bg-primary/90 hover:shadow-soft inline-flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-lg p-4 text-lg font-semibold text-white transition-all duration-200 ease-in-out md:h-[66px] md:w-max"
            >
              {/* Yazı: Mobile ve Desktop'ta göster, Tablet'te gizle */}
              <span
                className={clsx("whitespace-nowrap", searchButtonTextClassName)}
              >
                {tForm("bus.searchButton")}
              </span>

              {/* Turna icon: Mobile ve Desktop'ta göster, Tablet'te gizle */}
              <FontAwesomeIcon
                className={clsx(
                  "max-w-[24px] text-[24px]",
                  searchButtonTurnaIconClassName,
                )}
                icon={byPrefixAndName.fak["button-turna-icon"]}
              />

              {/* Büyüteç: Sadece Tablet'te göster */}
              <FontAwesomeIcon
                className={clsx(
                  "max-w-[32px] text-[32px]",
                  searchButtonMagnifierIconClassName,
                )}
                icon={byPrefixAndName.fak["search-button-icon"]}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Form;
